import { useState, useRef } from "react";
import { Plus } from "lucide-react";
import { SERVICES } from "@/data/content";
import { useReveal } from "@/hooks/useReveal";

function ServiceRow({ s, open, onToggle }) {
  const detailRef = useRef(null);
  return (
    <div
      data-testid={`service-${s.no}`}
      className="service-row reveal border-t border-[var(--line)]"
    >
      <button
        type="button"
        data-testid={`service-toggle-${s.no}`}
        aria-expanded={open}
        onClick={onToggle}
        className="w-full text-left grid grid-cols-12 gap-4 items-start py-8 sm:py-10 px-2 sm:px-4 cursor-pointer relative z-10"
      >
        <div className="col-span-2 sm:col-span-1">
          <span className="font-mono text-sm text-[var(--accent)]">{s.no}</span>
        </div>
        <h3 className="service-title col-span-10 sm:col-span-4 display text-2xl sm:text-3xl text-[var(--ink)]">
          {s.title}
        </h3>
        <p className="hidden sm:block col-span-12 sm:col-span-4 text-sm text-[var(--ink-dim)] leading-relaxed">
          {s.body}
        </p>
        <div className="col-span-12 sm:col-span-3 flex items-start sm:justify-end gap-3">
          <div className="hidden lg:flex flex-wrap gap-2 justify-end">
            {s.tags.map((t) => (
              <span key={t} className="chip rounded-full px-3 py-1 text-xs text-[var(--ink-dim)]">
                {t}
              </span>
            ))}
          </div>
          <span
            className="shrink-0 grid place-items-center h-8 w-8 rounded-full border border-[var(--line-strong)] text-[var(--ink)] transition-transform duration-500"
            style={{ transform: open ? "rotate(135deg)" : "rotate(0deg)" }}
          >
            <Plus className="h-4 w-4" strokeWidth={1.8} />
          </span>
        </div>
      </button>

      {/* expandable detail */}
      <div
        className="overflow-hidden transition-[max-height,opacity] duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] relative z-10"
        style={{
          maxHeight: open ? `${detailRef.current?.scrollHeight || 0}px` : "0px",
          opacity: open ? 1 : 0,
        }}
      >
        <div ref={detailRef} className="px-2 sm:px-4 pb-10 grid grid-cols-12 gap-4">
          <div className="hidden sm:block sm:col-span-1" />
          <p
            data-testid={`service-detail-${s.no}`}
            className="col-span-12 sm:col-span-8 text-[var(--ink-dim)] text-base leading-relaxed max-w-2xl"
          >
            {s.detail}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  const ref = useReveal();
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="services" ref={ref} data-testid="services-section" className="relative py-28 sm:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 reveal">
          <div>
            <span className="label">What we do</span>
            <h2 className="display mt-5 text-[2.25rem] sm:text-[3.25rem] max-w-2xl text-[var(--ink)]">
              The skillsets I bring{" "}
              <span className="serif-italic">to a build.</span>
            </h2>
          </div>
        </div>

        <div className="border-b border-[var(--line)]">
          {SERVICES.map((s, i) => (
            <ServiceRow
              key={s.no}
              s={s}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
